import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSession } from '../hooks/useSession'
import { relativeTime } from '../lib/relativeTime'

interface KeySummary {
  id: string
  name: string
  prefix: string
  createdAt: number
  lastUsedAt: number | null
}

interface MintResult {
  id: string
  raw: string
  prefix: string
  createdAt: number
}

export default function GetStarted() {
  const navigate = useNavigate()
  const { status } = useSession()
  const [keys, setKeys] = useState<KeySummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [minting, setMinting] = useState(false)
  const [minted, setMinted] = useState<MintResult | null>(null)
  const [copied, setCopied] = useState(false)
  const [snippetCopied, setSnippetCopied] = useState(false)

  const fetchKeys = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/api/keys', { credentials: 'include' })
      const data = await res.json() as { keys?: KeySummary[]; error?: string }
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      setKeys(data.keys ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load keys')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (status === 'signed-in') fetchKeys()
  }, [status, fetchKeys])

  async function mintKey(e: React.FormEvent) {
    e.preventDefault()
    setMinting(true)
    setError('')
    try {
      const res = await fetch('/api/keys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'cli' }),
      })
      const data = await res.json() as MintResult & { error?: string }
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      setMinted(data)
      setName('')
      fetchKeys()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to mint key')
    } finally {
      setMinting(false)
    }
  }

  async function revokeKey(id: string) {
    setError('')
    try {
      const res = await fetch(`/api/keys/${id}`, { method: 'DELETE', credentials: 'include' })
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(data.error ?? `HTTP ${res.status}`)
      }
      fetchKeys()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke')
    }
  }

  async function copyRaw() {
    if (!minted) return
    await navigator.clipboard.writeText(minted.raw)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function copySnippet() {
    if (!minted) return
    const snippet = `export INSTADASH_KEY=${minted.raw}\necho '{"hello":"world"}' | npx instadash push --name first`
    await navigator.clipboard.writeText(snippet)
    setSnippetCopied(true)
    setTimeout(() => setSnippetCopied(false), 2000)
  }

  // ── Auth gate ─────────────────────────────────────────────────────────
  if (status === 'probing') {
    return (
      <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
        <p className="muted mono" style={{ fontSize: 13 }}>Checking session…</p>
      </div>
    )
  }

  if (status === 'signed-out') {
    return (
      <div style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: 480 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>get started</div>
          <h1 className="h1">Sign in first.</h1>
          <p className="lede" style={{ marginTop: 14, marginBottom: 28 }}>
            API keys are tied to your account. Sign in, then come back here to mint one for the CLI.
          </p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/workspace')}>
            Go to sign in →
          </button>
        </div>
      </div>
    )
  }

  // ── Signed-in view ───────────────────────────────────────────────────
  return (
    <div style={{ minHeight: 'calc(100vh - 53px)', padding: '40px 0' }}>
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>get started</div>
        <h1 className="h-display" style={{ fontSize: 'clamp(36px, 4.5vw, 56px)' }}>
          Mint a key.<br/>
          <span className="accent">Pipe anything.</span>
        </h1>
        <p className="lede" style={{ fontSize: 16, marginTop: 16, marginBottom: 32 }}>
          API keys live forever until you revoke them. Use one per environment (laptop, CI, agent) so you can rotate without breaking everything.
        </p>

        {/* Mint form / one-time reveal */}
        {minted ? (
          <div className="card card-pad" style={{ marginBottom: 32, borderColor: 'var(--accent-line)' }}>
            <div className="eyebrow" style={{ marginBottom: 8, color: 'var(--accent)' }}>NEW KEY · COPY NOW</div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
              This is the only time you'll see the full key. Copy it now — we only store a hash.
            </p>
            <div onClick={copyRaw} style={{
              cursor: 'pointer', padding: '14px 16px',
              background: 'var(--bg)', border: '1px solid var(--line)',
              borderRadius: 6, display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
            }}>
              <span className="mono" style={{ fontSize: 13, color: 'var(--lime)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{minted.raw}</span>
              <span className="mono" style={{ fontSize: 11, color: copied ? 'var(--accent)' : 'var(--fg-3)' }}>
                {copied ? '✓ copied' : 'click to copy'}
              </span>
            </div>
            <div onClick={copySnippet} style={{ cursor: 'pointer', padding: '14px 16px', background: 'var(--bg)', border: '1px solid var(--line-soft)', borderRadius: 6 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Quickstart</div>
                <span className="mono" style={{ fontSize: 11, color: snippetCopied ? 'var(--accent)' : 'var(--fg-3)' }}>
                  {snippetCopied ? '✓ copied' : 'click to copy'}
                </span>
              </div>
              <pre className="mono" style={{ fontSize: 12, lineHeight: 1.7, color: 'var(--fg-1)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
{`export INSTADASH_KEY=${minted.raw}
echo '{"hello":"world"}' | npx instadash push --name first`}
              </pre>
            </div>
            <button className="btn btn-ghost btn-mono" onClick={() => setMinted(null)} style={{ marginTop: 18 }}>
              Done — hide key
            </button>
          </div>
        ) : (
          <form onSubmit={mintKey} className="card card-pad" style={{ marginBottom: 32 }}>
            <div className="row gap-8" style={{ alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 8 }}>
                  key name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="laptop, ci, agent…"
                  disabled={minting}
                  className="mono"
                  maxLength={64}
                  style={{
                    width: '100%', padding: '10px 14px', fontSize: 14,
                    background: 'var(--bg)', border: '1px solid var(--line)',
                    borderRadius: 6, color: 'var(--fg)', outline: 'none',
                  }}
                />
              </div>
              <button type="submit" disabled={minting} className="btn btn-primary btn-lg">
                {minting ? 'Minting…' : 'Generate key →'}
              </button>
            </div>
          </form>
        )}

        {error && (
          <div style={{ padding: '12px 16px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, color: 'var(--err)', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Existing keys */}
        <div className="eyebrow" style={{ marginBottom: 14 }}>your keys</div>
        {loading && <p className="muted mono" style={{ fontSize: 13 }}>Loading…</p>}
        {!loading && keys.length === 0 && (
          <div className="card card-pad" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <p className="muted" style={{ fontSize: 13 }}>No keys yet. Mint one above.</p>
          </div>
        )}
        {!loading && keys.length > 0 && (
          <div className="card" style={{ overflow: 'hidden' }}>
            {keys.map(k => (
              <div key={k.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr 0.6fr',
                gap: 16, padding: '14px 18px', alignItems: 'center',
                borderBottom: '1px solid var(--line-soft)',
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{k.name}</div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
                    created {relativeTime(k.createdAt)}
                  </div>
                </div>
                <div className="mono" style={{ fontSize: 13, color: 'var(--fg-2)' }}>{k.prefix}…</div>
                <div className="mono" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                  used {relativeTime(k.lastUsedAt)}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <button className="btn btn-ghost btn-mono" onClick={() => revokeKey(k.id)} style={{ fontSize: 12, padding: '4px 10px' }}>
                    revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="muted-2" style={{ fontSize: 12, marginTop: 32, lineHeight: 1.6 }}>
          The CLI reads <code className="mono">INSTADASH_KEY</code> from the environment, or accepts <code className="mono">--key</code>.
          You can also pass keys via <code className="mono">Authorization: Bearer</code> when calling the API directly.
        </p>
      </div>
    </div>
  )
}
